import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, yupResolver } from '@mantine/form';
import * as yup from 'yup';
import * as Sentry from '@sentry/react';

import { Button, Checkbox, Input, Spinner } from 'ui';

import appSettings from '../app-settings';
import { acceptUserInvite, getPublicUserInvite } from '../api/user-invites';
import RightContent from '../components/signup/RightContent';
import AlertMessage from '../components/alerts/AlertMessage';
import { authenticate, getNetworkError, getUserRole, isStrongPassword, sigInWithEmailAndPassword } from '../core/utils';
import { useAccount } from '../store/account/hooks';

const { redirectUri, appClient } = appSettings;

// Mirrors the server-side rule (1-50 chars, no angle brackets or control characters).
// eslint-disable-next-line no-control-regex
const NAME_REGEX = /^[^<>\u0000-\u001f\u007f]+$/;
const nameField = (label) =>
  yup
    .string()
    .trim()
    .required(`${label} is required.`)
    .max(50, 'Must be 50 characters or fewer.')
    .matches(NAME_REGEX, 'Invalid name.');

const validationSchema = yup.object({
  firstName: nameField('First name'),
  lastName: nameField('Last name'),
  password: yup
    .string()
    .test(
      'strong-password',
      'Password needs to be at least 8 characters long, contain at least 1 number, 1 special character, 1 uppercase letter and 1 lowercase letter.',
      (value) => isStrongPassword(value),
    )
    .required('Password is required.'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords do not match.')
    .required('Please confirm your password.'),
  agreeToTerms: yup.boolean().oneOf([true], 'You must agree to the terms and privacy policy.'),
});

const STATUS = { loading: 'loading', ready: 'ready', invalid: 'invalid' };

const AcceptInvite = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { setUserRole } = useAccount();
  const [status, setStatus] = useState(STATUS.loading);
  const [invite, setInvite] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const form = useForm({
    initialValues: { firstName: '', lastName: '', password: '', confirmPassword: '', agreeToTerms: false },
    validate: yupResolver(validationSchema),
    validateInputOnBlur: true,
  });
  const { setValues } = form;

  const loadInvite = useCallback(async () => {
    setStatus(STATUS.loading);
    try {
      const data = await getPublicUserInvite(inviteId);
      setInvite(data);
      setValues({ firstName: data.firstName ?? '', lastName: data.lastName ?? '' });
      setStatus(STATUS.ready);
    } catch (err) {
      // Every unusable link (unknown, expired, revoked, used) is the same 404; only report the unexpected.
      if (err?.response?.status !== 404) Sentry.captureException(err);
      setStatus(STATUS.invalid);
    }
    // setValues is stable for the lifetime of the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  const handleSubmit = async (values) => {
    setError('');
    setSubmitting(true);
    let email;
    try {
      ({ email } = await acceptUserInvite(inviteId, {
        password: values.password,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      }));
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        setStatus(STATUS.invalid);
      } else {
        if (code !== 400 && code !== 409) Sentry.captureException(err);
        setError(getNetworkError(err));
      }
      setSubmitting(false);
      return;
    }

    // The account exists now; a sign-in failure should send them to the login page, not retry the accept.
    // Either way the page unmounts, so `submitting` stays true to keep the form locked.
    try {
      await sigInWithEmailAndPassword(email ?? invite.email, values.password);
      setUserRole(getUserRole());
      navigate('/');
    } catch (err) {
      Sentry.captureException(err);
      authenticate(redirectUri, appClient, navigate, false);
    }
  };

  const logIn = () => authenticate(redirectUri, appClient, navigate, false);

  const renderInvalid = () => (
    <div className="max-w-[336px] space-y-4">
      <h1 className="text-h2 font-semibold">This invitation link isn&apos;t valid</h1>
      <p className="text-lg">
        It may have expired, been revoked, or already been used. Ask your organization&apos;s administrator to send you
        a new invitation.
      </p>
      <p className="text-lg">
        Already set up your account?{' '}
        <button type="button" onClick={logIn} className="text-primary-700">
          Log in
        </button>
      </p>
    </div>
  );

  const renderForm = () => {
    const org = invite.organizationName || 'your organization';
    return (
      <form noValidate className="w-full max-w-[336px] space-y-8" onSubmit={form.onSubmit(handleSubmit)}>
        <div className="space-y-4">
          <h1 className="text-h2 font-semibold">Join {org} on DonorSpring</h1>
          <p className="text-lg">
            {invite.inviterName ? `${invite.inviterName} invited you. ` : 'You have been invited. '}
            Set a password to finish creating your account.
          </p>
        </div>

        <div className="space-y-4">
          <Input id="email" label="Email" type="email" value={invite.email} readOnly disabled size="xl" />

          <div className="flex space-x-3">
            <div className="flex-1">
              <Input
                id="firstName"
                label="First Name"
                type="text"
                autoComplete="given-name"
                {...form.getInputProps('firstName')}
                required
                error={!!form.errors.firstName}
                helperText={form.errors.firstName}
                size="xl"
                disabled={submitting}
              />
            </div>
            <div className="flex-1">
              <Input
                id="lastName"
                label="Last Name"
                type="text"
                autoComplete="family-name"
                {...form.getInputProps('lastName')}
                required
                error={!!form.errors.lastName}
                helperText={form.errors.lastName}
                size="xl"
                disabled={submitting}
              />
            </div>
          </div>

          <Input
            id="password"
            label="Password"
            type="password"
            autoComplete="new-password"
            {...form.getInputProps('password')}
            required
            error={!!form.errors.password}
            helperText={form.errors.password}
            size="xl"
            disabled={submitting}
          />

          <Input
            id="confirmPassword"
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            {...form.getInputProps('confirmPassword')}
            required
            error={!!form.errors.confirmPassword}
            helperText={form.errors.confirmPassword}
            size="xl"
            disabled={submitting}
          />

          <Checkbox
            id="agree-to-terms"
            label={
              <label htmlFor="agree-to-terms" className="text-base">
                I agree to the&nbsp;
                <a
                  href="https://www.donorspring.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-700"
                >
                  Terms
                </a>
                &nbsp;and&nbsp;
                <a
                  href="https://www.donorspring.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-700"
                >
                  Privacy Policy
                </a>
              </label>
            }
            checked={form.values.agreeToTerms}
            onChange={(e) => form.setFieldValue('agreeToTerms', e.target.checked)}
            error={!!form.errors.agreeToTerms}
            helperText={form.errors.agreeToTerms}
            disabled={submitting}
          />
        </div>

        <Button
          title="Create Account"
          color="primary"
          size="xl"
          className="!block w-full"
          type="submit"
          disabled={submitting}
          loading={submitting}
        />
      </form>
    );
  };

  return (
    <>
      <div className="flex grow overflow-y-hidden bg-white-100">
        <div className="relative flex-1">
          {status === STATUS.loading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="size-full overflow-y-auto py-8">
              <div className="flex min-h-full w-full flex-col items-center justify-center px-6">
                {status === STATUS.invalid ? renderInvalid() : renderForm()}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1">
          <RightContent />
        </div>
      </div>

      <AlertMessage
        open={!!error}
        message={error || 'Oops, something went wrong!'}
        severity="error"
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setError('');
        }}
        autoHideDuration={null}
      />
    </>
  );
};

export default AcceptInvite;
